/**
 * Números do Mapa: a faixa do topo e o cabeçalho de cada sala.
 *
 * Tudo contado sobre o que PASSA no filtro (os números seguem o filtro) e só
 * com fato apontado — planilha ou app. Nada de estimativa: o gargalo é o
 * setor com mais ambientes atrasados, não uma previsão.
 *
 * Na v3 o setor é um CADASTRO, então nada aqui percorre lista fixa de códigos:
 * quem diz quais salas existem e em que ordem é o catálogo (`setores.js`), na
 * ordem do fluxo (`layout.js`).
 */
import { SEM_VALOR } from "./dados/filtros";
import { STATUS_AMBIENTE, tarefaAberta } from "./dados/vocabulario";
import { ordenarPeloFluxo } from "./layout";
import { contarSituacoes } from "./pessoas";
import { CATALOGO_VAZIO, SITUACAO } from "./setores";

const naFabrica = (d) => d.situacao === SITUACAO.SETOR && Boolean(d.setorId);

/** Sem posto, sem marceneiro da planilha e sem tarefa aberta com alguém. */
export function semResponsavel(demanda) {
  if (demanda.postoId || demanda.colaboradorPlanilhaId) return false;
  return !(demanda.tarefas || []).some((t) => t.colaborador_id);
}

/**
 * Cabeçalho da sala. `doSetor` = combinadas do setor (todas); `passa` =
 * Set<chaveLinha> do filtro ou null; `pecas` = peças desmembradas no setor.
 * Com filtro, os alertas contam só o que passa e `filtrados` diz quantos são.
 * → {total, filtrados, atrasadas, pausados, emProducao, semResponsavel, pecas}
 */
export function resumoDaSala(doSetor, passa = null, pecas = []) {
  const lista = passa ? doSetor.filter((d) => passa.has(d.chaveLinha)) : doSetor;
  return {
    total: doSetor.length,
    filtrados: passa ? lista.length : null,
    atrasadas: lista.filter((d) => d.atrasada).length,
    pausados: lista.filter((d) => d.statusApp === STATUS_AMBIENTE.PAUSADO).length,
    emProducao: lista.filter((d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO).length,
    semResponsavel: lista.filter(semResponsavel).length,
    pecas: (pecas || []).length,
  };
}

/**
 * Setor com mais atrasados (empate: o que tem mais ambientes; depois a ordem do
 * fluxo). `catalogo` = criarCatalogoSetores(...); sem setor cadastrado não há
 * gargalo.
 * → {setorId, nome, atrasadas} | null
 */
export function gargaloDaFabrica(demandas, catalogo = CATALOGO_VAZIO) {
  const contagem = ordenarPeloFluxo(catalogo.setores).map((setor, ordem) => {
    const doSetor = (demandas || []).filter((d) => naFabrica(d) && d.setorId === setor.id);
    return {
      setorId: setor.id,
      nome: setor.nome,
      ordem,
      atrasadas: doSetor.filter((d) => d.atrasada).length,
      total: doSetor.length,
    };
  });
  const [primeiro] = contagem.sort((a, b) => b.atrasadas - a.atrasadas || b.total - a.total || a.ordem - b.ordem);
  if (!primeiro || primeiro.atrasadas === 0) return null;
  return { setorId: primeiro.setorId, nome: primeiro.nome, atrasadas: primeiro.atrasadas };
}

function setoresDaPessoa(situacao, postosPorId) {
  const setores = new Set(situacao.colaborador.setor_ids || []);
  situacao.postoIds.forEach((id) => {
    const setorId = postosPorId.get(id)?.setor_id;
    if (setorId) setores.add(setorId);
  });
  return setores;
}

/**
 * Pessoas que entram na conta da faixa: o filtro de colaborador escolhe
 * quem; o de setor deixa só quem é do setor (cadastro ou posto).
 */
export function pessoasNoEscopo(situacoes, filtros, estado) {
  const postosPorId = new Map((estado?.postos || []).map((p) => [p.id, p]));
  const colaboradores = (filtros?.colaboradores || []).filter((v) => v !== SEM_VALOR);
  const setores = filtros?.setores || [];
  return [...situacoes.values()].filter((s) => {
    if (colaboradores.length && !colaboradores.includes(s.colaborador.id)) return false;
    if (!setores.length) return true;
    const daPessoa = setoresDaPessoa(s, postosPorId);
    return setores.some((setorId) => daPessoa.has(setorId));
  });
}

/**
 * Faixa do topo. `demandas`/`tarefas`/`pecas` já filtradas; `pessoas` =
 * situações no escopo (pessoasNoEscopo); `catalogo` = o dos setores.
 * → {emProducao, pausados, atrasados, tarefasAbertas, pecasFora,
 *    pessoas: {trabalhando, pausadas, livres, total}, gargalo}
 */
export function indicadoresDoMapa({ demandas = [], tarefas = [], pecas = [], pessoas = [], catalogo } = {}) {
  const fabrica = demandas.filter(naFabrica);
  const contagemPessoas = contarSituacoes(pessoas);
  return {
    emProducao: fabrica.filter((d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO).length,
    pausados: fabrica.filter((d) => d.statusApp === STATUS_AMBIENTE.PAUSADO).length,
    atrasados: fabrica.filter((d) => d.atrasada).length,
    tarefasAbertas: tarefas.filter(tarefaAberta).length,
    pecasFora: pecas.filter((p) => p.fora).length,
    pessoas: { ...contagemPessoas, total: pessoas.length },
    gargalo: gargaloDaFabrica(fabrica, catalogo),
  };
}
